import { execute } from '@/graphql/execute';
import { MeQuery, ThemePreference, UserViewerSettings } from '@/graphql/graphql';
import { updateThemeMutation, updateViewerSettingsMutation } from '@/mutations/user.mutation';
import { atom } from 'jotai';
import { atomWithStorage, createJSONStorage } from 'jotai/utils';

export interface iTokenUser {
  sessionId: string;
  exp: number;
  iat: number;
  iss: string;
  jti: string;
  sid: string;
  sub: string;
}

export const userAtom = atom<MeQuery['me'] | null>(null);

// Session storage for viewer settings when user is not set
const viewerSettingsStorageKey = '@actegon.gis.viewerSettings';
const storage = createJSONStorage<UserViewerSettings>(() => sessionStorage);

// Client-only point cloud loading settings. Not persisted to the server schema —
// stored in sessionStorage alongside the GraphQL-typed UserViewerSettings.
export type PointCloudLoadingSettings = {
  orthoPointSize: number;
  pointCloudLoadingDistance: number;
  pointCloudMinNodePixelSize: number;
};

const getDefaultValue = (): UserViewerSettings & PointCloudLoadingSettings => ({
  pointCloudPointSizeType: 'ADAPTIVE',
  pointCloudPointSize: 1.0,
  orthoPointSize: 2,
  pointCloudLoadingDistance: 500,
  pointCloudMinNodePixelSize: 100,
  showLayerSource: false,
  showLayerUnderType: false,
  showImagePOIs: true,
  showAllImagePOIs: false,
  imagePOIMaxDistance: 50,
  pointCloudPointBudget: 10_000_000,
  showPointCloudBoundingBoxes: false,
  googleStreetViewPoiColor: null,
  backgroundColor: null,
  customBaseMaps: [],
  actionPosition: 'BOTTOM',
});

const storageViewerSettingsAtom = atomWithStorage<UserViewerSettings & Partial<PointCloudLoadingSettings>>(
  viewerSettingsStorageKey,
  getDefaultValue(),
  storage,
);

export const userViewerSettingsAtom = atom(
  (get): UserViewerSettings & PointCloudLoadingSettings => {
    const user = get(userAtom);
    const defaults = getDefaultValue();
    if (user?.viewerSettings) {
      const stored = get(storageViewerSettingsAtom);
      // Only spread sessionStorage values that were explicitly set (not defaults).
      // Without this, default values (e.g. customBaseMaps: []) from a fresh
      // sessionStorage would overwrite server-persisted values.
      const hasStoredSession = sessionStorage.getItem(viewerSettingsStorageKey) !== null;
      const merged = hasStoredSession
        ? { ...defaults, ...user.viewerSettings, ...stored }
        : { ...defaults, ...user.viewerSettings };
      return {
        ...merged,
        orthoPointSize: merged.orthoPointSize ?? defaults.orthoPointSize,
        pointCloudLoadingDistance: merged.pointCloudLoadingDistance ?? defaults.pointCloudLoadingDistance,
        pointCloudMinNodePixelSize: merged.pointCloudMinNodePixelSize ?? defaults.pointCloudMinNodePixelSize,
      };
    }
    const stored = get(storageViewerSettingsAtom);
    return {
      ...defaults,
      ...stored,
      orthoPointSize: stored.orthoPointSize ?? defaults.orthoPointSize,
      pointCloudLoadingDistance: stored.pointCloudLoadingDistance ?? defaults.pointCloudLoadingDistance,
      pointCloudMinNodePixelSize: stored.pointCloudMinNodePixelSize ?? defaults.pointCloudMinNodePixelSize,
    };
  },
  (get, set, newSettings: UserViewerSettings & Partial<PointCloudLoadingSettings>) => {
    const user = get(userAtom);
    const defaults = getDefaultValue();

    set(storageViewerSettingsAtom, {
      ...newSettings,
      orthoPointSize: newSettings.orthoPointSize ?? defaults.orthoPointSize,
      pointCloudLoadingDistance: newSettings.pointCloudLoadingDistance ?? defaults.pointCloudLoadingDistance,
      pointCloudMinNodePixelSize: newSettings.pointCloudMinNodePixelSize ?? defaults.pointCloudMinNodePixelSize,
    });

    if (user) {
      set(userAtom, {
        ...user,
        viewerSettings: newSettings,
      });

      // orthoPointSize is client-only — don't send it to the server
      execute(updateViewerSettingsMutation, {
        input: {
          pointCloudPointSizeType: newSettings.pointCloudPointSizeType,
          pointCloudPointSize: newSettings.pointCloudPointSize,
          showLayerSource: newSettings.showLayerSource,
          showLayerUnderType: newSettings.showLayerUnderType,
          showImagePOIs: newSettings.showImagePOIs,
          showAllImagePOIs: newSettings.showAllImagePOIs,
          imagePOIMaxDistance: newSettings.imagePOIMaxDistance,
          pointCloudPointBudget: newSettings.pointCloudPointBudget,
          showPointCloudBoundingBoxes: newSettings.showPointCloudBoundingBoxes,
          googleStreetViewPoiColor: newSettings.googleStreetViewPoiColor,
          backgroundColor: newSettings.backgroundColor,
          customBaseMaps: newSettings.customBaseMaps,
          actionPosition: newSettings.actionPosition,
        },
      });
    }
  },
);

export type eTheme = ThemePreference;

export const userThemeAtom = atom(
  (get): eTheme | null => {
    const user = get(userAtom);
    if (!user) return null;
    return user.theme;
  },
  (get, set, theme: eTheme) => {
    const user = get(userAtom);
    if (user) {
      set(userAtom, { ...user, theme });
      execute(updateThemeMutation, { theme });
    }
  },
);
